export const BPROJ_EXTENSION = '.bproj';
export const BPROJ_MIME_TYPE = 'application/x-bigint-project+json';
export const BPROJT_EXTENSION = '.bprojt';
export const BPROJT_MIME_TYPE = 'application/x-bigint-project-template+json';
export const DEFAULT_MAX_PROJECT_BYTES = 10 * 1024 * 1024;

export interface BrowserTextFile {
  readonly name: string;
  readonly size: number;
  text(): Promise<string>;
}

/** 交換格式（XLSX）是二進位檔，選檔後要拿 bytes 而不是文字。 */
export interface BrowserBinaryFile {
  readonly name: string;
  readonly size: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export interface BrowserProjectFileGateway {
  pickProjectFile(): Promise<BrowserTextFile | null>;
  downloadTextFile(request: BrowserTextDownloadRequest): Promise<void>;
}

/**
 * 交換格式（MSPDI／XLSX／CSV）的檔案 I/O。與 `.bproj` 的 gateway 分開：
 * 那個只認一種副檔名，這裡每次都要指定 accept，而且要能輸出二進位。
 */
export interface BrowserExchangeFileGateway {
  pickFile(accept: string): Promise<(BrowserTextFile & BrowserBinaryFile) | null>;
  downloadTextFile(request: BrowserTextDownloadRequest): Promise<void>;
  downloadBinaryFile(request: BrowserBinaryDownloadRequest): Promise<void>;
}

export interface BrowserTextDownloadRequest {
  readonly contents: string;
  readonly suggestedName: string;
  readonly extension: string;
  readonly mimeType: string;
}

export interface BrowserBinaryDownloadRequest {
  readonly contents: Uint8Array;
  readonly suggestedName: string;
  readonly extension: string;
  readonly mimeType: string;
}

export interface BrowserDownloadEnvironment {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  clickDownload(url: string, filename: string): void;
}

export interface BrowserFileGatewayOptions {
  readonly document?: Document;
  readonly url?: Pick<typeof URL, 'createObjectURL' | 'revokeObjectURL'>;
}

export function ensureProjectExtension(suggestedName: string): string {
  return ensureFileExtension(suggestedName, BPROJ_EXTENSION, 'Untitled');
}

export function ensureFileExtension(
  suggestedName: string,
  extension: string,
  fallbackBasename = 'download',
): string {
  const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`;
  const filename = suggestedName.trim() || `${fallbackBasename}${normalizedExtension}`;
  return filename.toLowerCase().endsWith(normalizedExtension.toLowerCase())
    ? filename
    : `${filename}${normalizedExtension}`;
}

function assertWithinLimit(size: number, maxBytes: number): void {
  if (size > maxBytes) {
    throw new Error(`Project file exceeds the ${maxBytes}-byte browser limit.`);
  }
}

export async function readProjectText(
  file: BrowserTextFile,
  maxBytes = DEFAULT_MAX_PROJECT_BYTES,
): Promise<string> {
  assertWithinLimit(file.size, maxBytes);

  return file.text();
}

/** 與 `readProjectText()` 對稱：先擋大小再讀，不把整個檔案先吃進記憶體。 */
export async function readBinaryFile(
  file: BrowserBinaryFile,
  maxBytes = DEFAULT_MAX_PROJECT_BYTES,
): Promise<Uint8Array> {
  assertWithinLimit(file.size, maxBytes);

  return new Uint8Array(await file.arrayBuffer());
}

export function createProjectBlob(contents: string): Blob {
  return createTextBlob(contents, BPROJ_MIME_TYPE);
}

export function createTextBlob(contents: string, mimeType: string): Blob {
  return new Blob([contents], { type: mimeType });
}

export function createBinaryBlob(contents: Uint8Array, mimeType: string): Blob {
  // 複製一份是為了拿到明確以 ArrayBuffer 為底的 view；Blob 不接受可能來自
  // SharedArrayBuffer 的 view。
  return new Blob([new Uint8Array(contents)], { type: mimeType });
}

export function downloadProjectBlob(
  contents: string,
  suggestedName: string,
  environment: BrowserDownloadEnvironment,
): void {
  downloadTextBlob(
    {
      contents,
      suggestedName,
      extension: BPROJ_EXTENSION,
      mimeType: BPROJ_MIME_TYPE,
    },
    environment,
  );
}

export function downloadTextBlob(
  request: BrowserTextDownloadRequest,
  environment: BrowserDownloadEnvironment,
): void {
  downloadBlob(
    createTextBlob(request.contents, request.mimeType),
    request,
    environment,
  );
}

export function downloadBinaryBlob(
  request: BrowserBinaryDownloadRequest,
  environment: BrowserDownloadEnvironment,
): void {
  downloadBlob(
    createBinaryBlob(request.contents, request.mimeType),
    request,
    environment,
  );
}

function downloadBlob(
  blob: Blob,
  request: Pick<BrowserTextDownloadRequest, 'suggestedName' | 'extension'>,
  environment: BrowserDownloadEnvironment,
): void {
  const objectUrl = environment.createObjectURL(blob);

  try {
    environment.clickDownload(
      objectUrl,
      ensureFileExtension(request.suggestedName, request.extension),
    );
  } finally {
    environment.revokeObjectURL(objectUrl);
  }
}

/** 「開啟」同時接受範本：瀏覽器沒有原生選單的「從範本建立專案」，由 controller 依內容分流。 */
export function pickBrowserProjectFile(documentRef: Document): Promise<File | null> {
  return pickBrowserFile(documentRef, `${BPROJ_EXTENSION},${BPROJT_EXTENSION}`);
}

export function pickBrowserFile(documentRef: Document, accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = documentRef.createElement('input');
    input.type = 'file';
    input.accept = accept;

    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      input.removeEventListener('change', onChange);
      input.removeEventListener('cancel', onCancel);
      resolve(file);
    };
    const onChange = () => finish(input.files?.item(0) ?? null);
    const onCancel = () => finish(null);

    input.addEventListener('change', onChange);
    input.addEventListener('cancel', onCancel);
    input.click();
  });
}

export function createBrowserDownloadEnvironment(
  options: BrowserFileGatewayOptions = {},
): BrowserDownloadEnvironment {
  const documentRef = options.document ?? document;
  const urlRef = options.url ?? URL;

  return {
    createObjectURL: (blob) => urlRef.createObjectURL(blob),
    revokeObjectURL: (url) => urlRef.revokeObjectURL(url),
    clickDownload: (url, filename) => {
      const anchor = documentRef.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
    },
  };
}

export function createBrowserProjectFileGateway(
  options: BrowserFileGatewayOptions = {},
): BrowserProjectFileGateway {
  const documentRef = options.document ?? document;
  const downloadEnvironment = createBrowserDownloadEnvironment(options);

  return {
    pickProjectFile: () => pickBrowserProjectFile(documentRef),
    downloadTextFile: async (request) => {
      downloadTextBlob(request, downloadEnvironment);
    },
  };
}

export function createBrowserExchangeFileGateway(
  options: BrowserFileGatewayOptions = {},
): BrowserExchangeFileGateway {
  const documentRef = options.document ?? document;
  const downloadEnvironment = createBrowserDownloadEnvironment(options);

  return {
    pickFile: (accept) => pickBrowserFile(documentRef, accept),
    downloadTextFile: async (request) => {
      downloadTextBlob(request, downloadEnvironment);
    },
    downloadBinaryFile: async (request) => {
      downloadBinaryBlob(request, downloadEnvironment);
    },
  };
}
